import { useEffect, useState, useMemo } from "react";
import { api, formatTRY, monthLabel, errMsg } from "@/lib/api";
import { useMonth } from "@/context/MonthContext";
import { useAuth } from "@/context/AuthContext";
import { PieChart as PieIcon, Users as UsersIcon, Edit3, Check, X, Plus, Trash2, Search } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#3b82f6", "#a855f7", "#06b6d4"];

function colorFor(i) { return COLORS[i % COLORS.length]; }

export default function PartnerShares() {
  const { month } = useMonth();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [report, setReport] = useState(null);
  const [partners, setPartners] = useState([]);
  const [editCompany, setEditCompany] = useState(null); // {id, name}
  const [draft, setDraft] = useState({}); // partner_id -> number
  const [renaming, setRenaming] = useState(null); // partner id
  const [newName, setNewName] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addDraft, setAddDraft] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    const [r, p] = await Promise.all([
      api.get("/reports/monthly", { params: { month } }),
      api.get("/partners"),
    ]);
    setReport(r.data); setPartners(p.data);
  };
  useEffect(() => { load(); }, [month]);

  if (!report) return <div className="text-slate-400">Yükleniyor…</div>;

  const net = report.totals.net;

  const filteredCompanies = (() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return report.companies;
    return report.companies.filter(c =>
      (c.company_name || "").toLocaleLowerCase("tr").includes(q) ||
      partners.some(p => (p.name || "").toLocaleLowerCase("tr").includes(q) && (c.partner_shares?.[p.id] ?? 0) > 0)
    );
  })();

  const openEdit = (c) => {
    const existing = c.partner_shares || {};
    const d = {};
    partners.forEach(p => { d[p.id] = existing[p.id] != null ? Number(existing[p.id]) : (100 / partners.length); });
    setDraft(d);
    setEditCompany({ id: c.company_id, name: c.company_name });
  };

  const applyEqual = () => {
    const eq = +(100 / partners.length).toFixed(2);
    const d = {};
    partners.forEach(p => d[p.id] = eq);
    setDraft(d);
  };

  const draftTotal = Object.values(draft).reduce((s, v) => s + Number(v || 0), 0);

  const saveShares = async () => {
    if (Math.abs(draftTotal - 100) > 0.5) {
      toast.error(`Toplam %100 olmalı — şu an %${draftTotal.toFixed(2)}`);
      return;
    }
    try {
      await api.put(`/companies/${editCompany.id}/shares`, { shares: draft });
      toast.success("Paylar kaydedildi");
      setEditCompany(null);
      load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  const renamePartner = async (id) => {
    if (!newName.trim()) { setRenaming(null); return; }
    try {
      await api.put(`/partners/${id}`, { name: newName.trim() });
      toast.success("Ortak güncellendi");
      setRenaming(null); setNewName("");
      load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  const addPartner = async (e) => {
    e.preventDefault();
    try {
      await api.post("/partners", { name: addDraft.trim() });
      toast.success("Ortak eklendi");
      setAddOpen(false); setAddDraft("");
      load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  const removePartner = async (id) => {
    if (!window.confirm("Ortağı silmek istiyor musunuz? Firma payları sıfırlanacak.")) return;
    try {
      await api.delete(`/partners/${id}`);
      toast.success("Ortak silindi");
      load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  const pieData = partners.map((p, i) => ({
    name: p.name,
    value: Math.max(Number(report.partner_totals?.[p.id] || 0), 0),
    actual: Number(report.partner_totals?.[p.id] || 0),
    color: colorFor(i),
  }));

  return (
    <div className="space-y-6" data-testid="partners-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[.25em] text-slate-500 mb-1">Ortak Payları</div>
          <h1 className="text-3xl font-bold tracking-tight fk-heading">
            Ortak Kâr Dağıtımı — {monthLabel(month)}
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Her firma için ortakların payını özelleştirebilir, hakedişleri anında görebilirsiniz.
          </p>
        </div>
        {isAdmin && (
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-partner-btn" className="bg-indigo-600 hover:bg-indigo-500">
                <Plus className="w-4 h-4 mr-1.5"/> Ortak Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#131b2e] border-white/10">
              <DialogHeader><DialogTitle>Yeni Ortak</DialogTitle></DialogHeader>
              <form onSubmit={addPartner} className="space-y-4">
                <div className="space-y-2">
                  <Label>Ortak Adı</Label>
                  <Input required data-testid="partner-name-input"
                         value={addDraft} onChange={(e) => setAddDraft(e.target.value)}
                         className="bg-slate-900/60 border-slate-700"/>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="partner-submit"
                          className="bg-indigo-600 hover:bg-indigo-500">Kaydet</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="fk-card p-5 lg:col-span-1">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Konsolide Net Kâr</div>
          <div className={`text-3xl font-bold fk-mono mt-3 ${net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
            {formatTRY(net)}
          </div>
          <div className="mt-4 text-xs text-slate-500">
            Gelir {formatTRY(report.totals.income)} − Direkt Gider {formatTRY(report.totals.direct_expense)} − Ortak Payı {formatTRY(report.totals.manager_share)}
          </div>
        </div>

        <div className="fk-card p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <PieIcon className="w-4 h-4 text-indigo-400"/>
            <div className="text-sm font-semibold">Ortak Payı Dağılımı (Firma özel yüzdelerine göre)</div>
          </div>
          {pieData.every(d => d.value === 0) ? (
            <div className="h-[260px] flex items-center justify-center text-sm text-slate-500">Henüz hakediş yok.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={3}>
                  {pieData.map((d, i) => <Cell key={i} fill={d.color} stroke="transparent"/>)}
                </Pie>
                <Tooltip contentStyle={{ background:"#131b2e", border:"1px solid rgba(255,255,255,0.08)", borderRadius:8 }}
                         formatter={(v, n, p) => formatTRY(p.payload.actual)}/>
                <Legend wrapperStyle={{ fontSize: 12 }}/>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Partner cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {partners.map((p, i) => {
          const color = colorFor(i);
          const amount = Number(report.partner_totals?.[p.id] || 0);
          return (
            <div key={p.id} className="fk-card p-5" data-testid={`partner-card-${p.id}`}
                 style={{ borderColor: `${color}44` }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center border shrink-0"
                       style={{ background: `${color}22`, borderColor: `${color}55` }}>
                    <UsersIcon className="w-4 h-4" style={{ color }}/>
                  </div>
                  {renaming === p.id ? (
                    <div className="flex items-center gap-1">
                      <Input value={newName} onChange={(e)=>setNewName(e.target.value)}
                             className="h-8 bg-slate-900/60 border-slate-700 text-sm"/>
                      <Button size="sm" variant="ghost" onClick={()=>renamePartner(p.id)}
                              className="h-8 w-8 p-0 text-emerald-400 hover:bg-emerald-500/10"><Check className="w-4 h-4"/></Button>
                      <Button size="sm" variant="ghost" onClick={()=>setRenaming(null)}
                              className="h-8 w-8 p-0 text-slate-400 hover:bg-white/5"><X className="w-4 h-4"/></Button>
                    </div>
                  ) : (
                    <div className="text-sm font-semibold text-white truncate">{p.name}</div>
                  )}
                </div>
                {isAdmin && renaming !== p.id && (
                  <div className="flex items-center">
                    <Button size="sm" variant="ghost" data-testid={`rename-partner-${p.id}`}
                            onClick={() => { setRenaming(p.id); setNewName(p.name); }}
                            className="h-8 w-8 p-0 text-slate-500 hover:text-indigo-300 hover:bg-indigo-500/10">
                      <Edit3 className="w-3.5 h-3.5"/>
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`del-partner-${p.id}`}
                            onClick={()=>removePartner(p.id)}
                            className="h-8 w-8 p-0 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10">
                      <Trash2 className="w-3.5 h-3.5"/>
                    </Button>
                  </div>
                )}
              </div>
              <div className="mt-5">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-mono">Aylık Hakediş</div>
                <div className={`text-2xl font-semibold fk-mono mt-1 ${amount >= 0 ? "text-white" : "text-rose-400"}`}>
                  {formatTRY(amount)}
                </div>
              </div>
            </div>
          );
        })}
        {partners.length === 0 && (
          <div className="col-span-full fk-card p-10 text-center text-slate-500">
            Ortak yok. Ekleyerek başlayın.
          </div>
        )}
      </div>

      {/* Per-company shares table */}
      <div className="fk-card p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold">Firma × Ortak Payı Matrisi ve Hakediş</div>
            <div className="text-xs text-slate-500">Her firma için payları özelleştirmek için satırdaki <span className="text-indigo-300 font-medium">Düzenle</span>'ye tıklayın.</div>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <Input data-testid="partner-search" value={search} onChange={(e)=>setSearch(e.target.value)}
                   placeholder="Firma veya hissedar ara…"
                   className="h-9 pl-9 w-[260px] bg-slate-900/60 border-slate-700" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[.02] text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Firma</th>
                <th className="text-right px-5 py-3 font-medium">Net Kâr</th>
                {partners.map(p => (
                  <th key={p.id} className="text-right px-5 py-3 font-medium">
                    <div>{p.name}</div>
                    <div className="text-[9px] text-slate-500 fk-mono normal-case tracking-normal">yüzde · hakediş</div>
                  </th>
                ))}
                {isAdmin && <th className="w-24"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredCompanies.map(c => (
                <tr key={c.company_id} className="hover:bg-white/[.02]" data-testid={`shares-row-${c.company_id}`}>
                  <td className="px-5 py-3 font-medium text-white">{c.company_name}</td>
                  <td className={`px-5 py-3 text-right fk-mono ${c.net_profit >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {formatTRY(c.net_profit)}
                  </td>
                  {partners.map(p => {
                    const pct = Number(c.partner_shares?.[p.id] || 0);
                    const amt = Number(c.partner_split?.[p.id] || 0);
                    return (
                      <td key={p.id} className="px-5 py-3 text-right">
                        <div className="fk-mono text-xs text-slate-400">%{pct.toFixed(2)}</div>
                        <div className={`fk-mono text-sm font-medium ${amt >= 0 ? "text-indigo-300" : "text-rose-300"}`}>
                          {formatTRY(amt)}
                        </div>
                      </td>
                    );
                  })}
                  {isAdmin && (
                    <td className="px-5 py-3 text-right">
                      <Button size="sm" variant="ghost" data-testid={`edit-shares-${c.company_id}`}
                              onClick={() => openEdit(c)}
                              className="text-indigo-300 hover:bg-indigo-500/10 h-8">
                        <Edit3 className="w-3.5 h-3.5 mr-1"/> Düzenle
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
              {report.companies.length === 0 && (
                <tr><td colSpan={partners.length + 3} className="px-5 py-10 text-center text-slate-500">Firma yok.</td></tr>
              )}
              {report.companies.length > 0 && filteredCompanies.length === 0 && (
                <tr><td colSpan={partners.length + 3} className="px-5 py-10 text-center text-slate-500">"{search}" için eşleşen firma yok.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Shares Dialog */}
      <Dialog open={!!editCompany} onOpenChange={(o) => !o && setEditCompany(null)}>
        <DialogContent className="bg-[#131b2e] border-white/10 max-w-md" data-testid="edit-shares-dialog">
          <DialogHeader>
            <DialogTitle>{editCompany?.name} — Ortak Payları</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {partners.map((p, i) => (
              <div key={p.id} className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: colorFor(i) }}/>
                <Label className="flex-1 text-sm">{p.name}</Label>
                <div className="flex items-center gap-1">
                  <Input type="number" step="0.01" min="0" max="100"
                         data-testid={`share-input-${p.id}`}
                         value={draft[p.id] ?? ""} onChange={(e) => setDraft({ ...draft, [p.id]: Number(e.target.value) })}
                         className="w-24 h-9 text-right fk-mono bg-slate-900/60 border-slate-700"/>
                  <span className="text-slate-500 text-sm">%</span>
                </div>
              </div>
            ))}
            <div className={`mt-4 p-3 rounded-lg border text-sm flex items-center justify-between ${
              Math.abs(draftTotal - 100) <= 0.5
                ? "bg-emerald-500/5 border-emerald-500/25 text-emerald-300"
                : "bg-rose-500/5 border-rose-500/25 text-rose-300"
            }`}>
              <span>Toplam</span>
              <span className="fk-mono font-semibold">%{draftTotal.toFixed(2)}</span>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={applyEqual} data-testid="apply-equal-btn"
                    className="bg-slate-900/60 border-slate-700">Eşit Dağıt</Button>
            <Button onClick={saveShares} data-testid="save-shares-btn"
                    className="bg-indigo-600 hover:bg-indigo-500">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
